import React, { useEffect, useRef, useState } from "react";
import {
  Link,
  useNavigate,
  useSearchParams,
  useLocation,
} from "react-router-dom";
import { ReactComponent as Logo } from "../../assets/images/logo.svg";
import { ReactComponent as LinkIcon } from "../../../src/assets/icons/links.svg";
import { ReactComponent as EyeOpen } from "../../../src/assets/icons/eye-open.svg";
import { ReactComponent as EyeClose } from "../../../src/assets/icons/eye-close.svg";
import { ReactComponent as Spinner } from "../../assets/icons/spinner.svg";
import { useDispatch, useSelector } from "react-redux";
import {
  useGetUserQuery,
  useLoginMutation,
  useLogOutUserMutation,
} from "../../store/auth/usersApiSlice";
import {
  clearIdentityScopedState,
  clearPendingReauthIdentity,
  clearUserInfo,
  hideIdleModal,
  hideLoginModal,
  logout,
  setExpireTime,
  setToken,
  setUserInfo,
} from "../../store/auth/authSlice";
import { AUTH_EVENTS, postAuthEvent } from "../../utils/authChannel";
import { CommonToast } from "../../components/common/Toast";
import { validateEmail } from "../../utils/Helper";
import ReactGA from "react-ga4";
import { useGetMagicTokenMutation } from "../../store/Company/companyApiSlice";
import { plansInformation } from "../../constants/constants";
import SessionConflictModal from "../../components/common/SessionConflictModal";

export default function Login({ modalLogin = false, preventRoute = false }) {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [formData, setFormData] = useState({});
  const [showSessionPopup, setShowSessionPopup] = useState(false);
  // Device and last-seen time of the OTHER session, so the conflict dialog can
  // name what it is about to sign out instead of saying "another device".
  const [sessionConflict, setSessionConflict] = useState(null);
  const {
    token,
    pendingReauthEmail,
  } = useSelector((state) => state.auth);
  // TIP-511 — inside the "Login Required" modal the session belongs to a known
  // account, so the email is pinned to it. `readOnly` is UX; what makes the pin
  // hold against a devtools edit is that the field becomes a CONTROLLED input
  // and `handleLogin` reads `formData.email`, never the DOM node. The mismatch
  // check in `handleLogin` is the backstop for that invariant.
  const lockedEmail = modalLogin ? pendingReauthEmail : null;
  const location = useLocation();
  const redirectStep = location?.state?.redirectToStep ?? null;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const isArticle = params.get('app') === "article";
  const articleSlug = params.get('slug');
  const inviteCodeParams = params.get('invite_code');
  const [inviteCode, setInviteCode] = useState(localStorage.getItem('invite_code'));
  const promptPage = location.pathname.includes("prompt");
  const dashboardPage = location.pathname.includes("dashboard");
  const lastVisited = localStorage.getItem('lastVisited') ?? "";
  const [error, setError] = useState("");
  const [login, { isLoading }] = useLoginMutation();
  const [logOutUser] = useLogOutUserMutation();
  const [userToken, setUserToken] = useState("");
  const [magicToken, setMagicToken] = useState("");
  const [getMagicToken, { isLoading: isMagicLoading }] = useGetMagicTokenMutation()
  const { userInfo } = useSelector((state) => state.auth);
  const { data, refetch, isSuccess, isError } = useGetUserQuery(userToken, {
    skip: !userToken,
    refetchOnMount: true,
  });
  const [getSearchParams] = useSearchParams();
  // TIP-599 — set once handleLogin has decided where a fresh sign-in should
  // land (claim flow, stored lastVisited, plans resume, ...). The mount effect
  // below is the "already signed in, don't sit on /login" guard; without this
  // it also fires moments later — when userInfo arrives — and overwrites that
  // decision with a bounce to Home, losing the claim intent.
  const loginRoutedRef = useRef(false);

  const articleRedirect = (token) => {
    const baseUrl = `${process.env.REACT_APP_K_HUB_URL}/magic/${token}`;
    // Only append slug if it's available
    const url = articleSlug ? `${baseUrl}?slug=${articleSlug}` : baseUrl;
    window.open(url, "_blank");

  }
  // TIP-511 — seed the pinned email so a plain "enter password + submit" works.
  useEffect(() => {
    if (lockedEmail) {
      setFormData((prev) => ({ ...prev, email: lockedEmail }));
    }
  }, [lockedEmail]);

  useEffect(() => {
    if (inviteCodeParams) {
      setInviteCode(inviteCodeParams);
    }
  }, [inviteCodeParams])

  useEffect(() => {
    if (userInfo) {
      if (userInfo?.is_first_time_setted_password) {
        navigate(`/reset-password?token=${userToken}&email=${userInfo?.email}`);
      }
      else {
        if (!isArticle && !preventRoute && !loginRoutedRef.current) {
          navigate(promptPage ? `/gpt-prompts/access` : `/`);
        }
      }
    }
  }, [navigate, userInfo]);

  useEffect(() => {
    const redirect = async () => {

      if (isArticle) {
        const res = await getMagicToken({ token });
        if (res?.data?.token) {
          articleRedirect(res?.data?.token);
          navigate(dashboardPage ? `/dashboard` : `/`);
        } else {
          dispatch(clearUserInfo());
        }
      }
    };
    redirect();
  }, [isArticle, token])

  useEffect(() => {
    if (isSuccess) {
      dispatch(setUserInfo({ ...data }));
    }
  }, [data, dispatch, isSuccess]);

  function togglePasswordVisibility() {
    setIsPasswordVisible((prevState) => !prevState);
  }

  const handleChange = (event) => {
    // TIP-511 — while re-authenticating a known session the email is fixed.
    if (lockedEmail && event.target.name === "email") return;
    setFormData({ ...formData, [event.target.name]: event.target.value });
  };

  /**
   * TIP-511 — leave the dead session behind entirely. This is the supported
   * route for a second user on the same browser: a full logout (which does
   * clear everything) followed by a normal sign-in.
   */
  const handleSwitchAccount = async () => {
    // END THE SERVER SESSION TOO, not just ours.
    // This used to clear Redux and localStorage and stop there, while calling
    // itself "a full logout". The backend kept the OutstandingToken alive, and
    // sign-in is single-session — so the very next login answered
    // ALREADY_LOGGED_IN and the customer landed in the session-conflict
    // dialog instead of signing in. Switching account appeared to be broken
    // when what was broken was the logout behind it.
    // Best effort: the token that got us here may already be rejected, in
    // which case the endpoint refuses and there is nothing more we can do
    // from the client. Clearing locally must happen regardless, or the
    // customer is stuck in a modal they cannot leave.
    try {
      // `token` is the LIVE session from Redux, not the local `userToken`
      // state — that one is only set after a successful sign-in here, and is
      // empty at the moment somebody switches account.
      await logOutUser({ token }).unwrap();
    } catch (err) {
      // Already expired, already blacklisted, or offline. Not fatal here.
    }

    // Tell the other tabs before clearing this one. They share the cookies
    // that were just revoked, so without this they keep rendering a signed-in
    // UI until their next 401 — which on an idle page may never come.
    postAuthEvent(AUTH_EVENTS.SIGNED_OUT);

    dispatch(hideLoginModal());
    dispatch(hideIdleModal());
    dispatch(logout());
    dispatch(clearUserInfo());
    setFormData({});
    setError("");
    navigate("/login");
  };

  const handleLogin = async (e, forced = false) => {
    e.preventDefault();
    try {
      if (!validateEmail(formData.email)) {
        setError("Invalid Email, Please enter a valid email")
      } else {
        setError("");
        const submittedEmail = formData.email.toLowerCase();

        /**
         * TIP-511 — backstop. The controlled field should make this
         * unreachable; if it ever is reached (the field made uncontrolled
         * again, or `lockedEmail` changing after the user typed), a mismatch
         * must never authenticate a second identity into the surviving
         * session. Drop to a clean logout instead — the supported way to
         * switch accounts on a shared browser.
         */
        if (lockedEmail && submittedEmail !== lockedEmail) {
          handleSwitchAccount();
          CommonToast.error(
            "Please log in as " + lockedEmail + ", or log out to use another account."
          );
          return;
        }

        const previousEmail = userInfo?.email
          ? userInfo.email.toLowerCase()
          : null;
        const splittedData = lastVisited ? lastVisited?.split("/") : [];
        const res = await login({
          email: formData.email.toLowerCase(),
          password: formData.password,
          ...(forced && { force_login: true }),
          ...(isArticle && { return_magic_token: true }),
          ...(inviteCode && { invite_code: inviteCode }),
          ...(splittedData?.[1] === "claim" && { validate_id: splittedData[2] }),
          ...(modalLogin && { referance: splittedData[2] }),
        }).unwrap();

        if (res?.status) {
          ReactGA.event({
            category: 'Login',
            action: 'Login Flow:Attempted to Login; Login Failed',
            label: 'Attempted to Login; Login Failed',
          });
          if (res?.status === "LOGIN_FAILED") {
            // STOP HERE. This used to fall through to the success path below,
            // which read `res.token` (undefined), dispatched setToken(undefined),
            // raised a misleading "Token is required" toast and navigated to
            // /login — remounting this component and wiping the very error just
            // set. A customer with a wrong password saw a bounce and no reason.
            setError(res?.message ? res?.message : "Validation Failed")
            return;
          } else if (res?.status === "ALREADY_LOGGED_IN") {
            setSessionConflict({
              device: res?.device ?? null,
              last_seen_at: res?.last_seen_at ?? null,
            });
            setShowSessionPopup(true);
            return;
          }
        }
        if (res?.reset_token) {
          ReactGA.event({
            category: 'Login',
            action: 'Login Flow:Asked user to reset your password',
            label: 'Asked user to reset your password',
          });
          navigate(`/reset-password?token=${res?.reset_token}&email=${formData.email.toLowerCase()}`)
        } else {
          /**
           * TIP-511 — defence in depth. Whichever route got us here, if the
           * account that just signed in is not the one this browser was last
           * holding state for, that state belongs to someone else: drop the
           * identity-scoped keys before any of it can be rendered. Same-user
           * re-login takes the else branch and keeps its in-progress work.
           */
          if (previousEmail && previousEmail !== submittedEmail) {
            dispatch(clearIdentityScopedState());
          } else {
            dispatch(clearPendingReauthIdentity());
          }

          /* Success is no longer "the response contained a token". */
          const signedIn = Boolean(res) && !res?.status;
          const token = signedIn;

          dispatch(setExpireTime(res?.expire_time))
          setUserToken(token);
          dispatch(setToken());

          // Tell the other tabs, and say WHO — a tab holding user A's cached
          // data needs to know that user B now owns this browser.
          if (signedIn) {
            postAuthEvent(AUTH_EVENTS.SIGNED_IN, submittedEmail);
          }

          if (token) {
            ReactGA.event({
              category: 'Login',
              action: 'Login Flow:Attempted to Login; Login Success',
              label: 'Attempted to Login; Login Success',
            });
            if (isSuccess) {
              dispatch(setUserInfo({ ...data }));
            }

            // TIP-511 — a re-login inside the "Login Required" modal is finished
            // the moment the token lands: close the modal and stay on the page.
            // Closing used to hang off `isSuccess`, which is the getUser query's
            // flag. On this first pass it is still false — its `userToken` was
            // set a few lines above and React has not re-rendered yet — so the
            // modal stayed open showing the same form, and the customer had to
            // enter the same credentials a second time before a by-then-resolved
            // query let it close. The `preventRoute` branch further down closed
            // it correctly, but only when `lastVisited` was empty; with a stale
            // `lastVisited` in localStorage the navigate() branch won instead and
            // the modal was left standing.
            setShowSessionPopup(false);
            dispatch(hideLoginModal());
            dispatch(hideIdleModal());

            if (preventRoute) {
              loginRoutedRef.current = true;
              return;
            }

            const slug = localStorage.getItem('slug');
            // This sign-in owns the redirect from here on — see loginRoutedRef.
            loginRoutedRef.current = true;
            if (redirectStep) {
              navigate("/sign-up", {
                state: { redirectToStep: 6 },
              })
            } else if (slug && lastVisited == "/plans") {
              let plan = plansInformation.filter((item) => item.slug === slug);
              const companyDataString = localStorage.getItem("payasyougoCompanyID");
              window.scrollTo(0, 0);
              navigate("/plans/paymentdetails", {
                state: {
                  planDetails: plan[0],
                  company_id: JSON.parse(JSON.stringify(companyDataString)),
                },
              });
            }
            else if (lastVisited) {
              if (res?.validate_id === true) {
                navigate(`/search/${splittedData[2]}`)
              } else {
                navigate(`${lastVisited}`);
              }
            } else {
              if (isArticle) {
                articleRedirect(res.magic_token)
              }
              if (preventRoute) {
                dispatch(hideLoginModal());
              } else {
                navigate(dashboardPage ? `/dashboard` : `/`);
              }
            }
          } else {
            CommonToast.error("Token is required");
            const url = isArticle && articleSlug ? `/login?app=article&slug=${articleSlug}` : "/login?app=article"
            navigate(dashboardPage ? "/login/dashboard" : isArticle ? url : "/login");
          }
        }
      }
    } catch (error) {
      setError(
        error?.data
          ? Object.values(error?.data)[0]
          : "An error occurred. Please try again later."
      );
    }
  };

  const handleForgotPassword = () => {
    ReactGA.event({
      category: 'Fogot Password',
      action: 'Login Flow:Clicked on forgot password button',
      label: 'Clicked on forgot password button',
    });
  }

  const handleCreateNewAccount = () => {
    ReactGA.event({
      category: 'Create New Account',
      action: 'Login Flow:Clicked on Create New Account button',
      label: 'Clicked on Create New Account button',
    });
  }

  return (
    <>
      {showSessionPopup && (
        <SessionConflictModal
          setShowSessionPopup={setShowSessionPopup}
          handleForceLogin={(e) => handleLogin(e, true)}
          conflict={sessionConflict}
        />
      )}
      <div
        id="login"
        className={`${modalLogin ? "py-0" : "py-12"}flex h-full flex-1 flex-col justify-between px-4`}
      >
        <div className={`${modalLogin ? "mt-0" : "mt-10"} md:mx-auto md:w-full md:max-w-xl sm:max-w-sm sm:w-full sm:mx-auto`}>
          {!modalLogin && <div className="md:mx-auto">
            <Link to="/">
              <div className="flex justify-center items-center md:mx-auto" style={{ margin: '10px' }}>
                <Logo />
              </div>
            </Link>
            <p className="text-light text-para3 mt-2">
              Don't have an account? {" "}
              <Link to="/sign-up" onClick={handleCreateNewAccount} className="font-bold">
                Create account
              </Link>
            </p>
          </div>}
          <form className={`${modalLogin ? "space-y-4" : "space-y-6 mt-12"}`} onSubmit={handleLogin}>
            <div>
              <label
                htmlFor="email"
                className={`block text-sm font-medium leading-6 text-[#3C4848] text-left ${modalLogin ? "text-[12px]" : "text-[16px]"}`}
              >
                Email
              </label>
              <div className="mt-2">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  placeholder="Enter your email address"
                  onChange={handleChange}
                  required
                  {...(lockedEmail
                    ? {
                        // TIP-511 — pinned to the account being re-authenticated.
                        // Disabled per the agreed approach on the ticket; the
                        // value is still submitted because `handleLogin` reads
                        // `formData`, not the form's own field collection.
                        value: lockedEmail,
                        disabled: true,
                        "aria-disabled": true,
                        title:
                          "You are signing back in to this account. Log out to use a different one.",
                      }
                    : {})}
                  // style={modalLogin ? { border: "none", backgroundColor: "#FAFAFA" } : {}}
                  className={`block w-full py-3 px-3 text-gray-900 shadow-sm placeholder:text-gray-400 placeholder:text-[12px] sm:text-sm sm:leading-6 ${modalLogin ? "rounded-md" : "rounded-md"} ${lockedEmail ? "bg-[#F1F1F1] cursor-not-allowed text-gray-600 disabled:opacity-100" : ""}`}
                />
              </div>
              {lockedEmail && (
                <p className="text-[#787878] text-[12px] mt-1 text-left">
                  Signing back in to this account.{" "}
                  <button
                    type="button"
                    onClick={handleSwitchAccount}
                    className="text-[#28475C] font-bold underline"
                  >
                    Not you? Log out
                  </button>
                </p>
              )}
            </div>

            <div>
              <div className="flex items-center justify-between">
                <label
                  htmlFor="password"
                  className={`block text-sm font-medium leading-6 text-[#3C4848] ${modalLogin ? "text-[12px]" : "text-[16px]"}`}
                >
                  Password
                </label>
              </div>
              <div className="mt-2 relative">
                <input
                  id="password"
                  name="password"
                  type={isPasswordVisible ? "text" : "password"}
                  autoComplete="current-password"
                  placeholder="Enter your password"
                  required
                  onChange={handleChange}
                  // style={modalLogin ? { border: "none", backgroundColor: "#FAFAFA" } : {}}
                  className={`block w-full py-3 px-3 text-gray-900 shadow-sm  placeholder:text-gray-400 placeholder:text-[12px] sm:text-sm sm:leading-6 ${modalLogin ? "rounded-md" : "rounded-md"}`}
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                  {isPasswordVisible ? (
                    <EyeOpen onClick={togglePasswordVisibility} />
                  ) : (
                    <EyeClose onClick={togglePasswordVisibility} />
                  )}
                </div>
              </div>
              {!modalLogin && <div className="text-sm text-left py-2">
                <Link
                  to="/reset-password"
                  onClick={handleForgotPassword}
                  className="text-left text-para3 font-bold"
                >
                  Forgot Your password?
                </Link>
              </div>}
            </div>
            {error && <div className="text-left text-red-500">{error}</div>}
            <div>
              <button
                type="submit"
                disabled={isLoading}
                className={`flex w-full justify-center rounded-md px-3 py-3 text-sm font-semibold leading-6 text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 ${isLoading ? "cursor-not-allowed bg-disable" : "cursor-pointer bg-primary"}`} style={{ marginBottom: '15px' }}
              >
                {isLoading ? <Spinner /> : "Log in"}
              </button>
            </div>
          </form>
          {getSearchParams.get("guest") === "true" ? (
            <div className="mt-5 mb-5">
            </div>
          ) : null}
        </div>

        {!modalLogin ? <div>
          <p>{`Copyright @ ${new Date().getFullYear()} TREVEX, Inc`} </p>
          <Link
            to="/privacy-policy"
            className="inline-flex items-center justify-center mr-2 gap-2 font-bold"
          >
            Privacy Policy <LinkIcon />
          </Link>
        </div> : <p className="text-[#787878] text-[12px] mt-2">
          Don't have an account? {" "}
          <Link className="text-[#28475C] font-bold" to="/sign-up" onClick={handleCreateNewAccount}>
            Register now
          </Link>
        </p>}
      </div>
    </>
  );
}
